import { useRouter } from '@tanstack/react-router'
import { Skeleton } from 'antd'
import { CreateVersionModal } from 'features/libra/dashboard/CreateVersionModal'
import type { TicketDashboardSettings } from 'features/libra/dashboard/ticketDashboard/libra/settings/dashboardSettings'
import { useLibraTicketDashboard } from 'features/libra/dashboard/ticketDashboard/libra/useLibraTicketDashboard'
import { TicketDashboardView } from 'features/libra/dashboard/ticketDashboard/TicketDashboardView'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import { createLibraSearch } from 'routes/libra/-searchParams'

interface LibraTicketDashboardProps {
  dashboardId: string
  settings: TicketDashboardSettings
  tickets: ReadonlyArray<LibraTicket>
  workflows: ReadonlyArray<LibraWorkflow>
  isLoading: boolean
  hasCreateVersionPermission: boolean
  onRefresh: () => Promise<void>
}

/**
 * The skeleton shows only until the first load. Later reloads keep the last
 * loaded workflows instead: the workflow query re-keys whenever the group's
 * set of workflows changes, and unmounting then would reset the filters.
 */
export const LibraTicketDashboard: FC<LibraTicketDashboardProps> = ({
  isLoading,
  workflows,
  onRefresh,
  ...props
}) => {
  const router = useRouter()
  const [loadedWorkflows, setLoadedWorkflows] =
    useState<ReadonlyArray<LibraWorkflow>>()
  if (!isLoading && loadedWorkflows !== workflows) {
    setLoadedWorkflows(workflows)
  }
  const { config, cards } = useLibraTicketDashboard({
    ...props,
    workflows: loadedWorkflows ?? workflows,
  })

  const [ticketForNewVersion, setTicketForNewVersion] = useState<LibraTicket>()
  const ticketsById = useMemo(
    () => new Map(props.tickets.map((ticket) => [ticket.id, ticket])),
    [props.tickets]
  )
  const handleCreateVersion = useCallback(
    (ticketId: string) => setTicketForNewVersion(ticketsById.get(ticketId)),
    [ticketsById]
  )
  const handleCloseVersionModal = useCallback(
    () => setTicketForNewVersion(undefined),
    []
  )
  const handleVersionCreated = useCallback(
    async (newTicketId: string, sourceTicket: LibraTicket) => {
      try {
        await onRefresh()
      } catch (error) {
        console.warn('Failed to refresh dashboard before navigation', error)
      }
      const orgId = sourceTicket.tags.organization
      if (typeof orgId !== 'string' || !orgId) return
      router.navigate({
        to: '/libra',
        search: createLibraSearch({
          orgId,
          workflowId: sourceTicket.workflowId,
          ticket: newTicketId,
        }),
      })
    },
    [onRefresh, router]
  )

  if (!loadedWorkflows) return <Skeleton active />
  return (
    <>
      <TicketDashboardView
        key={config.id}
        config={config}
        cards={cards}
        onCreateVersion={handleCreateVersion}
      />
      <CreateVersionModal
        isOpen={Boolean(ticketForNewVersion)}
        sourceTicket={ticketForNewVersion}
        onClose={handleCloseVersionModal}
        onSuccess={handleVersionCreated}
      />
    </>
  )
}
